/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC } from 'react';

import { I18n } from '@coze-arch/i18n';
import { Spin } from '@coze-arch/coze-design';

import { type PublishStep } from './publish-steps';
import {
  PublishScopeSelector,
  type PublishScopeValue,
} from './publish-scope-selector';

import styles from './index.module.less';

export interface PublishFormPanelProps {
  loading?: boolean;
  name: string;
  description: string;
  iconUrl?: string;
  botId?: string;
  llmLabel: string;
  ontologyLabel: string;
  versionNote: string;
  scopeValue: PublishScopeValue;
  step: PublishStep;
  onVersionNoteChange: (value: string) => void;
  onScopeChange: (value: PublishScopeValue) => void;
  onOpenDept: () => void;
  onBack: () => void;
  onPublish: () => void;
}

export const PublishFormPanel: FC<PublishFormPanelProps> = ({
  loading = false,
  name,
  description,
  iconUrl,
  botId,
  llmLabel,
  ontologyLabel,
  versionNote,
  scopeValue,
  step,
  onVersionNoteChange,
  onScopeChange,
  onOpenDept,
  onBack,
  onPublish,
}) => (
  <>
    <div className={styles.card}>
      <div className={styles.cardTitle}>{I18n.t('abr_app_info_title')}</div>
      {loading ? (
        <div className={styles.metaLoading}>
          <Spin />
        </div>
      ) : (
        <>
          <div className={styles.agentRow}>
            {iconUrl ? (
              <img src={iconUrl} alt="" className={styles.agentIcon} />
            ) : (
              <div
                className={`${styles.agentIcon} ${styles.agentIconPlaceholder}`}
              >
                {(name || 'A').slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className={styles.agentMain}>
              <div className={styles.agentName}>{name || '—'}</div>
              {botId ? (
                <div className={styles.agentMeta}>bot_id: {botId}</div>
              ) : null}
              {description ? (
                <p className={styles.agentDesc}>{description}</p>
              ) : null}
              <span className={styles.tagReadonly}>
                {I18n.t('abr_app_readonly_tag')}
              </span>
            </div>
          </div>
          <dl className={styles.metaGrid}>
            <div className={styles.metaItem}>
              <dt>{I18n.t('abr_app_info_llm')}</dt>
              <dd>{llmLabel}</dd>
            </div>
            <div className={styles.metaItem}>
              <dt>{I18n.t('abr_app_info_ontology')}</dt>
              <dd>{ontologyLabel}</dd>
            </div>
          </dl>
        </>
      )}
    </div>
    <div className={styles.card}>
      <label className={styles.fieldLabel} htmlFor="version-note">
        {I18n.t('abr_app_version_note')}
      </label>
      <textarea
        id="version-note"
        className={styles.textarea}
        value={versionNote}
        onChange={e => onVersionNoteChange(e.target.value)}
        placeholder={I18n.t('abr_app_version_note_placeholder')}
      />
    </div>
    <PublishScopeSelector
      value={scopeValue}
      onChange={onScopeChange}
      onOpenDept={onOpenDept}
    />
    <div className={styles.actions}>
      <button type="button" className={styles.btnGhost} onClick={onBack}>
        {I18n.t('abr_app_cancel')}
      </button>
      <button
        type="button"
        className={styles.btnPrimary}
        disabled={loading || step === 'publishing'}
        onClick={onPublish}
      >
        {step === 'publishing'
          ? I18n.t('abr_app_publishing')
          : I18n.t('abr_app_publish_btn')}
      </button>
    </div>
  </>
);
